/**
 * Single source of truth for workforce roles: display labels, grouping,
 * whether the role is benchmark-backed (auto-estimated → feeds ROI), and
 * per-role suggested specialty sub-items.
 *
 * Web, API, and the design app consume this registry instead of local
 * `WORKFORCE_ROLES` copies. Benchmark roles MUST stay in sync with
 * WORKFORCE_RATIOS in ../benchmarks/reference-data.ts (asserted by tests).
 */

export type WorkforceGroup = "clinical" | "revenue_cycle" | "non_clinical";

export interface WorkforceRoleDef {
  /** Stable role key, e.g. "physician". */
  key: string;
  /** Display label, e.g. "Physician". */
  label: string;
  /** Display grouping. */
  group: WorkforceGroup;
  /** true => ratio-backed, auto-estimated, feeds the ROI/baseline engine. */
  isBenchmark: boolean;
  /** Free-form suggestions offered when breaking this role into sub-items. */
  suggestedSubItems: string[];
}

export const WORKFORCE_GROUP_ORDER: WorkforceGroup[] = [
  "clinical",
  "revenue_cycle",
  "non_clinical",
];

export const WORKFORCE_GROUP_LABELS: Record<WorkforceGroup, string> = {
  clinical: "Clinical",
  revenue_cycle: "Revenue cycle & access",
  non_clinical: "Non-clinical & operations",
};

/**
 * Ordered list — display order within a group follows this array. The
 * non-clinical template rows are a starting point for SMEs to iterate over.
 */
export const WORKFORCE_ROLE_DEFS: WorkforceRoleDef[] = [
  // ── Clinical ──────────────────────────────────────────────
  {
    key: "physician",
    label: "Physician",
    group: "clinical",
    isBenchmark: true,
    suggestedSubItems: [
      "Cardiology",
      "OB-GYN",
      "Hospitalist",
      "Emergency Medicine",
      "Anesthesiology",
      "Primary Care",
      "General Surgery",
      "Radiology",
    ],
  },
  {
    key: "nurse",
    label: "Nurse",
    group: "clinical",
    isBenchmark: true,
    suggestedSubItems: [
      "ICU",
      "Emergency Department",
      "Med-Surg",
      "Operating Room",
      "Labor & Delivery",
      "Outpatient Clinic",
    ],
  },
  {
    key: "cdi_specialist",
    label: "CDI Specialist",
    group: "clinical",
    isBenchmark: true,
    suggestedSubItems: [
      "Inpatient CDI",
      "Outpatient CDI",
      "Quality / DRG review",
    ],
  },
  {
    key: "perioperative_admin",
    label: "Perioperative Admin",
    group: "clinical",
    isBenchmark: true,
    suggestedSubItems: ["Pre-op scheduling", "OR coordination", "PACU admin"],
  },
  // ── Revenue cycle & access ────────────────────────────────
  {
    key: "coder",
    label: "Coder",
    group: "revenue_cycle",
    isBenchmark: true,
    suggestedSubItems: [
      "Inpatient (IP)",
      "Outpatient (OP)",
      "Profee",
      "ED coding",
      "Surgery coding",
    ],
  },
  {
    key: "pa_staff",
    label: "Prior Auth Staff",
    group: "revenue_cycle",
    isBenchmark: true,
    suggestedSubItems: [
      "Imaging auth",
      "Surgical auth",
      "Pharmacy auth",
      "Specialty referrals",
    ],
  },
  {
    key: "call_center_agent",
    label: "Call Center Agent",
    group: "revenue_cycle",
    isBenchmark: true,
    suggestedSubItems: [
      "Scheduling",
      "Billing support",
      "Patient triage",
      "Referrals",
    ],
  },
  {
    key: "denial_management",
    label: "Denial Management",
    group: "revenue_cycle",
    isBenchmark: true,
    suggestedSubItems: [
      "Clinical appeals",
      "Technical denials",
      "Underpayments",
    ],
  },
  {
    key: "scheduling",
    label: "Scheduling",
    group: "revenue_cycle",
    isBenchmark: true,
    suggestedSubItems: [
      "Surgical scheduling",
      "Imaging scheduling",
      "Clinic scheduling",
    ],
  },
  {
    key: "eligibility_verification",
    label: "Eligibility Staff",
    group: "revenue_cycle",
    isBenchmark: true,
    suggestedSubItems: [
      "Pre-registration",
      "Insurance verification",
      "Financial counseling",
    ],
  },
  {
    key: "revenue_integrity",
    label: "Revenue Integrity",
    group: "revenue_cycle",
    isBenchmark: true,
    suggestedSubItems: [
      "Charge capture",
      "CDM maintenance",
      "Audit & compliance",
    ],
  },
  // ── Non-clinical & operations (template rows, no benchmark data) ──
  {
    key: "exec_admin_leadership",
    label: "Exec & admin leadership",
    group: "non_clinical",
    isBenchmark: false,
    suggestedSubItems: [
      "C-suite",
      "Department directors",
      "Administrative support",
    ],
  },
  {
    key: "it_health_informatics",
    label: "IT / Health informatics",
    group: "non_clinical",
    isBenchmark: false,
    suggestedSubItems: [
      "Clinical informatics",
      "Infrastructure",
      "Help desk",
      "Application analysts",
    ],
  },
  {
    key: "facilities_operations",
    label: "Facilities / operations",
    group: "non_clinical",
    isBenchmark: false,
    suggestedSubItems: [
      "Environmental services",
      "Biomed / clinical engineering",
      "Security",
      "Food services",
    ],
  },
];

const BY_KEY: Map<string, WorkforceRoleDef> = new Map(
  WORKFORCE_ROLE_DEFS.map((def) => [def.key, def]),
);

export function getWorkforceRoleDef(key: string): WorkforceRoleDef | undefined {
  return BY_KEY.get(key);
}

/** Registry label, else a humanized fallback of the key (for custom roles). */
export function getWorkforceRoleLabel(key: string): string {
  return BY_KEY.get(key)?.label ?? key;
}

/** Registry group; unknown (custom) keys default to non-clinical. */
export function getWorkforceRoleGroup(key: string): WorkforceGroup {
  return BY_KEY.get(key)?.group ?? "non_clinical";
}

export function isBenchmarkRole(key: string): boolean {
  return BY_KEY.get(key)?.isBenchmark ?? false;
}
